import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from '../../../services/clinical-onboarding.service';
import { ToastService } from '../../../services/general/toast.service';
import {
  ClinicalOnboardingStateActionsService,
  ClinicianStateSubject,
} from '../../../services/clinical-onboarding-state-actions.service';
import {
  ClinicianDashboardKpis,
  ClinicianDashboardRow,
  ClinicianMacroStatus,
  FiscalRegime,
  clinicalOnboardingI18nKeySuffix,
} from '../../../models/clinical-onboarding.model';
import {
  ClinicalOnboardingClinicianDetailDialogComponent
} from '../clinical-onboarding-clinician-detail-dialog/clinical-onboarding-clinician-detail-dialog.component';
import {
  ModalRegisterClinicalOnboardingCandidatesComponent
} from '../../../modals/modal-register-clinical-onboarding-candidates/modal-register-clinical-onboarding-candidates.component';
import {
  ModalImportClinicalOnboardingCandidatesComponent
} from '../../../modals/modal-import-clinical-onboarding-candidates/modal-import-clinical-onboarding-candidates.component';

interface DashboardFilters {
  search: string;
  status: string;
  paid: 'ALL' | 'PAID' | 'UNPAID';
  verification: 'ALL' | 'ok' | 'ko' | 'neutral';
  fiscalRegime: string;
  imported: 'ALL' | 'IMPORTED' | 'NOT_IMPORTED';
}

const DEFAULT_FILTERS: DashboardFilters = {
  search: '',
  status: 'ALL',
  paid: 'ALL',
  verification: 'ALL',
  fiscalRegime: 'ALL',
  imported: 'ALL',
};

/** Valore del filtro regime fiscale per i clinici che non ne hanno ancora indicato uno. */
const FISCAL_REGIME_NONE = 'NONE';

/**
 * DBEK-407: dashboard di avanzamento onboarding clinico (KPI + Tabella-1, piano §4) -
 * lato gestore, mostrata come nuova tab in survey-details-page. Colonne di
 * macro-stato (DBEK-409) e semaforo di verifica (DBEK-408) si aggiungono sopra
 * questa base in ticket successivi, stesso schema di dipendenza gia' seguito lato
 * backend (branch-per-ticket con rebase quando serve davvero).
 */
@Component({
  selector: 'app-clinical-onboarding-dashboard',
  templateUrl: './clinical-onboarding-dashboard.component.html',
  styleUrls: ['./clinical-onboarding-dashboard.component.scss'],
  standalone: false,
})
export class ClinicalOnboardingDashboardComponent implements OnChanges {
  @Input() surveyId: string | number;

  loading = false;
  kpis: ClinicianDashboardKpis | null = null;
  dataSource = new MatTableDataSource<ClinicianDashboardRow>([]);

  // Colonne sempre visibili (identita', stato, azioni) + colonne opzionali
  // nascondibili dal menu "Colonne" - stesso identico pattern di
  // survey-team.component.ts (columnSettings/toggleColumn/rebuildColumns), che
  // risolve lo stesso problema (tabella con troppe colonne per stare comoda a
  // schermo, qui 7 colonne dati oltre a identita'/stato/azioni).
  private readonly mandatoryColumns = ['select', 'name', 'macro_status'];
  displayedColumns: string[] = [];
  columnSettings: { key: string; label: string; visible: boolean }[] = [
    { key: 'is_paid', label: 'components.clinical-onboarding.dashboard.column-paid', visible: true },
    { key: 'verification_ok', label: 'components.clinical-onboarding.dashboard.column-verification', visible: true },
    { key: 'fiscal_regime', label: 'components.clinical-onboarding.wizard.field-fiscal-regime', visible: true },
    { key: 'net_amount_indicative', label: 'components.clinical-onboarding.dashboard.column-net', visible: true },
    { key: 'documents_received_at', label: 'components.clinical-onboarding.dashboard.column-documents-received-at', visible: false },
    { key: 'pa_authorization_status', label: 'components.clinical-onboarding.dashboard.column-pa-authorization', visible: false },
    { key: 'manual_participation_override', label: 'components.clinical-onboarding.dashboard.column-override', visible: false },
    { key: 'pa_communication_status', label: 'components.clinical-onboarding.dashboard.column-pa-communication', visible: false },
  ];

  // 'ALL' come sentinella, non null: stesso identico pattern gia' collaudato in
  // survey-team.component.ts (filters.responseStatus) - un mat-option con
  // [value]="null" non mostra il testo selezionato nel trigger del mat-select
  // (bug osservato: il campo restava vuoto invece di leggere "Tutti gli stati").
  // Ricerca e filtri sono tutti lato client: la lista di una survey e' gia'
  // interamente caricata in una sola chiamata (i KPI non dipendono dai filtri).
  filters: DashboardFilters = { ...DEFAULT_FILTERS };
  readonly ClinicianMacroStatus = ClinicianMacroStatus;
  readonly FiscalRegime = FiscalRegime;

  // Sollecito manuale ammesso solo per chi deve ancora completare il pacchetto -
  // stesso identico gate lato backend (ClinicalOnboardingController::remindCandidates):
  // il testo della notifica ("pacchetto ancora incompleto o non firmato") sarebbe
  // fuorviante per un candidato gia' SIGNED/oltre, o EXPIRED/EXCLUDED.
  private readonly REMINDABLE_STATUSES: string[] = [ClinicianMacroStatus.WAITING, ClinicianMacroStatus.IN_PROGRESS];
  selection = new SelectionModel<ClinicianDashboardRow>(true, []);
  sendingReminder = false;
  // Riga su cui e' in corso un cambio di stato (pagamento/esclusione/forzatura).
  stateActionRowId: number | null = null;

  constructor(
    private clinicalOnboardingService: ClinicalOnboardingService,
    private dialog: MatDialog,
    private toastService: ToastService,
    private stateActions: ClinicalOnboardingStateActionsService,
    private translateService: TranslateService,
  ) {
    this.rebuildColumns();
    this.dataSource.filterPredicate = (row, rawFilters) => this.matchesFilters(row, JSON.parse(rawFilters));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.surveyId && this.surveyId) {
      this.load();
    }
  }

  private rebuildColumns(): void {
    const visibleOptionalKeys = this.columnSettings.filter((s) => s.visible).map((s) => s.key);
    this.displayedColumns = [...this.mandatoryColumns, ...visibleOptionalKeys, 'actions'];
  }

  toggleColumn(setting: { key: string; label: string; visible: boolean }): void {
    setting.visible = !setting.visible;
    this.rebuildColumns();
  }

  load(): void {
    if (!this.surveyId) {
      return;
    }
    this.loading = true;
    this.clinicalOnboardingService.getDashboard(
      this.surveyId,
      null,
      (data) => {
        this.loading = false;
        this.kpis = data.kpis;
        this.dataSource.data = data.clinicians || [];
        this.applyFilters();
        this.selection.clear();
      },
      () => {
        this.loading = false;
      },
    );
  }

  isRemindable(row: ClinicianDashboardRow): boolean {
    return this.REMINDABLE_STATUSES.includes(row.macro_status);
  }

  get remindableRows(): ClinicianDashboardRow[] {
    return this.dataSource.filteredData.filter((row) => this.isRemindable(row));
  }

  get isAllRemindableSelected(): boolean {
    const remindable = this.remindableRows;
    return remindable.length > 0 && remindable.every((row) => this.selection.isSelected(row));
  }

  toggleSelectAllRemindable(): void {
    if (this.isAllRemindableSelected) {
      this.selection.clear();
    } else {
      this.selection.select(...this.remindableRows);
    }
  }

  /** Sollecito per la selezione multipla corrente. */
  sendBulkReminder(): void {
    const candidateIds = this.selection.selected.map((row) => row.candidate_id);
    this.sendReminder(candidateIds);
  }

  /** Sollecito per un singolo candidato, dal bottone in tabella - non richiede selezione. */
  sendSingleReminder(row: ClinicianDashboardRow): void {
    this.sendReminder([row.candidate_id]);
  }

  private sendReminder(candidateIds: number[]): void {
    if (candidateIds.length === 0 || this.sendingReminder) {
      return;
    }
    this.sendingReminder = true;
    this.clinicalOnboardingService.remindCandidates(
      this.surveyId,
      candidateIds,
      (result) => {
        this.sendingReminder = false;
        this.toastService.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('components.clinical-onboarding.dashboard.reminder-sent', { count: result.sent }),
        );
        this.selection.clear();
      },
      () => {
        this.sendingReminder = false;
      },
    );
  }

  private matchesFilters(row: ClinicianDashboardRow, f: DashboardFilters): boolean {
    const term = f.search.trim().toLowerCase();
    if (term && !`${row.name || ''} ${row.email || ''}`.toLowerCase().includes(term)) {
      return false;
    }
    if (f.status !== 'ALL' && row.macro_status !== f.status) {
      return false;
    }
    if (f.paid === 'PAID' && !row.is_paid) {
      return false;
    }
    if (f.paid === 'UNPAID' && row.is_paid) {
      return false;
    }
    if (f.verification !== 'ALL' && this.verificationDisplayFor(row).statusClass !== f.verification) {
      return false;
    }
    if (f.fiscalRegime !== 'ALL' && (row.fiscal_regime || FISCAL_REGIME_NONE) !== f.fiscalRegime) {
      return false;
    }
    if (f.imported === 'IMPORTED' && !row.imported) {
      return false;
    }
    if (f.imported === 'NOT_IMPORTED' && row.imported) {
      return false;
    }
    return true;
  }

  /** Riapplica i filtri e toglie dalla selezione i clinici non piu' visibili (il sollecito multiplo non deve toccare righe nascoste). */
  applyFilters(): void {
    this.dataSource.filter = JSON.stringify(this.filters);
    const visible = new Set(this.dataSource.filteredData);
    const hidden = this.selection.selected.filter((row) => !visible.has(row));
    if (hidden.length > 0) {
      this.selection.deselect(...hidden);
    }
  }

  get hasActiveFilters(): boolean {
    return JSON.stringify(this.filters) !== JSON.stringify(DEFAULT_FILTERS);
  }

  resetFilters(): void {
    this.filters = { ...DEFAULT_FILTERS };
    this.applyFilters();
  }

  readonly FISCAL_REGIME_NONE = FISCAL_REGIME_NONE;

  /**
   * DBEK-392 (ridisegno "candidato"): candidateId, non team_member_id - quest'ultimo
   * e' null finche' il candidato non e' stato importato nel vero team member della
   * survey (piano §"registrare i futuri medici prima che la survey sia partita"), ma
   * il dettaglio deve restare consultabile anche prima, dato che
   * ClinicalOnboardingController::getClinicianDetail risolve sempre a partire dal
   * Candidate. panelClass 'medium-dialog' invece di width/maxHeight inline (audit
   * UI/UX), stessa chrome standard di modal-add-project. 'clinician-detail-dialog-panel'
   * aggiunto in piu' (feedback utente: "stessa dimensione [...] per tutti i tab") -
   * fissa larghezza/altezza del dialog stesso in _common.scss, cosi' passare da
   * "Dettaglio" (contenuto lungo) a "Storico attivita'" (spesso piu' corto) non fa
   * piu' saltare le dimensioni del dialog - 'medium-dialog' da solo lascia il dialog
   * auto-dimensionato sul contenuto del tab corrente.
   */
  openClinicianDetail(row: ClinicianDashboardRow): void {
    const dialogRef = this.dialog.open(ClinicalOnboardingClinicianDetailDialogComponent, {
      data: { surveyId: this.surveyId, candidateId: row.candidate_id },
      panelClass: ['medium-dialog', 'clinician-detail-dialog-panel'],
    });
    // Un'azione sul documento nel dialog puo' far cambiare macro_status/verifica -
    // ricarico la Tabella-1 alla chiusura cosi' resta coerente senza duplicare la
    // logica di ricalcolo, che vive gia' server-side.
    dialogRef.afterClosed().subscribe(() => this.load());
  }

  /**
   * Azioni di stato del gestore, dal menu a tre puntini della riga: pagamento
   * (registra / modifica / annulla), esclusione / riammissione, forzatura della
   * partecipazione - la logica e' nel servizio condiviso con la tab "Dati
   * contrattuali" del dialog. Il macro-stato lo ricalcola il server; qui basta
   * ricaricare la tabella. Per un clinico gia' pagato restano solo modifica e
   * annullamento del pagamento.
   */
  /** Il clinico di una riga, nella forma che le azioni di stato condivise si aspettano. */
  private stateSubjectFor(row: ClinicianDashboardRow): ClinicianStateSubject {
    return {
      surveyId: this.surveyId,
      candidateId: row.candidate_id,
      name: row.name || row.email,
      isPaid: row.is_paid,
      isExcluded: row.is_excluded,
      hasParticipationOverride: row.manual_participation_override,
      suggestedAmount: row.net_amount ?? row.net_amount_indicative ?? null,
      payment: row.is_paid
        ? { paid_amount: row.paid_amount, payment_date: row.payment_date, cro_reference: row.payment_cro_reference }
        : null,
    };
  }

  private stateHooksFor(row: ClinicianDashboardRow) {
    return {
      onBusy: (busy: boolean) => (this.stateActionRowId = busy ? row.candidate_id : null),
      onDone: () => this.load(),
    };
  }

  openRegisterPayment(row: ClinicianDashboardRow): void {
    this.stateActions.registerPayment(this.stateSubjectFor(row), this.stateHooksFor(row));
  }

  openEditPayment(row: ClinicianDashboardRow): void {
    this.stateActions.editPayment(this.stateSubjectFor(row), this.stateHooksFor(row));
  }

  cancelPayment(row: ClinicianDashboardRow): void {
    this.stateActions.cancelPayment(this.stateSubjectFor(row), this.stateHooksFor(row));
  }

  toggleExclusion(row: ClinicianDashboardRow): void {
    this.stateActions.toggleExclusion(this.stateSubjectFor(row), this.stateHooksFor(row));
  }

  toggleParticipationOverride(row: ClinicianDashboardRow): void {
    this.stateActions.toggleParticipationOverride(this.stateSubjectFor(row), this.stateHooksFor(row));
  }

  i18nKeySuffix(value: string): string {
    return clinicalOnboardingI18nKeySuffix(value);
  }

  /**
   * Bug: la colonna "Verifica" mostrava sempre la spunta verde/rossa in base al
   * solo verification_ok, che pero' vale true anche quando l'IBAN non e' ancora
   * stato inserito (nessun problema rilevato != verifica realmente superata).
   * Stessa logica a 3 stati gia' usata in ClinicalOnboardingClinicianDetailDialogComponent.verificationDisplay:
   * un override esplicito del gestore vince sempre, altrimenti "nessun IBAN"
   * mostra uno stato neutro invece di un falso positivo verde.
   */
  verificationDisplayFor(row: ClinicianDashboardRow): { icon: string; tooltipKey: string; statusClass: 'ok' | 'ko' | 'neutral' } {
    if (row.verification_override !== null) {
      return row.verification_override
        ? { icon: 'check_circle', tooltipKey: 'components.clinical-onboarding.clinician-detail.verification-override-valid', statusClass: 'ok' }
        : { icon: 'cancel', tooltipKey: 'components.clinical-onboarding.clinician-detail.verification-override-invalid', statusClass: 'ko' };
    }

    if (!row.verification_ok) {
      return { icon: 'cancel', tooltipKey: 'components.clinical-onboarding.dashboard.verification-ko', statusClass: 'ko' };
    }

    if (!row.has_iban) {
      return { icon: 'info', tooltipKey: 'components.clinical-onboarding.clinician-detail.verification-no-iban-yet', statusClass: 'neutral' };
    }

    return { icon: 'check_circle', tooltipKey: 'components.clinical-onboarding.dashboard.verification-ok', statusClass: 'ok' };
  }

  /**
   * DBEK-395 (ridisegno "candidato"): disponibile fin da qui, che la survey sia
   * ancora in bozza o gia' avviata (piano §"registrare i futuri medici prima che la
   * survey sia effettivamente partita") - a differenza di importClinicianDetail,
   * questa azione non richiede che il candidato esista gia'.
   */
  openRegisterCandidates(): void {
    const dialogRef = this.dialog.open(ModalRegisterClinicalOnboardingCandidatesComponent, {
      data: { surveyId: this.surveyId },
      panelClass: 'medium-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.load();
      }
    });
  }

  /**
   * DBEK-395: importazione MANUALE, in aggiunta al trigger automatico che scatta
   * quando la survey esce dalla bozza (vedi OrganizationSurvey::booted() lato
   * backend) - per chi vuole importare in anticipo un sottoinsieme gia' pronto.
   */
  openImportCandidates(): void {
    const dialogRef = this.dialog.open(ModalImportClinicalOnboardingCandidatesComponent, {
      data: { surveyId: this.surveyId },
      panelClass: 'medium-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.load();
      }
    });
  }
}
